import { useCallback, useMemo, useState, useEffect } from "react";
import useGetReportCompanyOrClientDetails from "hooks/organization/reports/types/useGetReportCompanyOrClientDetails";
import { capitalCase } from "change-case";
import JobModal from "components/JobModal/JobModal";
import usePaginatedResources from "hooks/usePaginatedResources";

export default function useReportTableCompanyOrClientDetails(startDate, endDate, companyId = null) {
  const [openModal, setOpenModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const [getReportCompanyOrClientDetails, { data, loading, error }] =
    useGetReportCompanyOrClientDetails(false); // No success message for filtering/pagination

  const handleOpenModal = useCallback((jobId: string) => {
    setSelectedJobId(jobId);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedJobId(null);
    setOpenModal(false);
  }, []);

  // Format currency for display
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const paginate = useCallback(
    ({ page = 1, first = 25 } = {}) => {
      const variables = {
        startDate,
        endDate,
        companyId,
        page,
        first,
      };
      getReportCompanyOrClientDetails({ variables });
    },
    [startDate, endDate, companyId, getReportCompanyOrClientDetails]
  );

  const pagination = usePaginatedResources({
    paginate,
    paginatorInfo: data?.getReportCompanyOrClientDetails?.paginatorInfo,
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "Company Name",
        accessor: "companyName",
        Cell: ({ value }) => value || "N/A",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Company ID",
        accessor: "companyId",
        Cell: ({ value }) => value || "N/A",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Company Status",
        accessor: "companyStatus",
        Cell: ({ value }) => capitalCase(value ?? "N/A"),
        disableSortBy: true,
      },
      {
        Header: "Proposals count",
        accessor: "proposalsCount",
        Cell: ({ value }) => value ?? "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Proposals Won Count",
        accessor: "proposalsWonCount",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Win rate",
        accessor: "winRate",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        align: "right",
      },
      {
        Header: "Proposals Amount Sum",
        accessor: "proposalsWonAmountTotal",
        Cell: ({ value }) => formatCurrency(value) ?? "",
        disableSortBy: true,
        align: "right",
      },
      {
        Header: "Percentage of Total Organization Revenue",
        accessor: "orgRevenuePercentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        align: "right",
      },
    ];
  }, [handleOpenModal]);

  const tableData = useMemo(() => {
    return {
      columns,
      rows: data?.getReportCompanyOrClientDetails?.data || [],
      modal: <JobModal jobId={selectedJobId} open={openModal} onClose={handleCloseModal} />,
    };
  }, [columns, data, selectedJobId, openModal, handleCloseModal]);

  return {
    columns,
    data,
    loading,
    error,
    tableData,
    pagination,
    getReportCompanyOrClientDetails: paginate,
  } as const;
}
